import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAdminAuth } from "@/context/AdminAuthContext";

interface ContactRequest {
  id: string;
  nombre: string;
  empresa: string | null;
  email: string | null;
  tipo_proyecto: string | null;
  estado: string;
  created_at: string;
}

interface ActivityLog {
  id: string;
  usuario: string | null;
  accion: string;
  elemento: string | null;
  created_at: string;
}

interface DashboardStats {
  proyectos: number;
  proyectosPublicados: number;
  servicios: number;
  redes: number;
  imagenes: number;
  contenido: number;
  mensajes: number;
}

const quickAccess = [
  { label: "Nuevo proyecto", icon: "ri-add-circle-line", to: "/admin/proyectos" },
  { label: "Editar portada", icon: "ri-home-5-line", to: "/admin/contenido" },
  { label: "Administrar proyectos", icon: "ri-folder-3-line", to: "/admin/proyectos" },
  { label: "Editar servicios", icon: "ri-stack-line", to: "/admin/servicios" },
  { label: "Redes sociales", icon: "ri-share-line", to: "/admin/redes" },
  { label: "Configuración", icon: "ri-settings-4-line", to: "/admin/marca" },
];

function timeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "ahora mismo";
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `hace ${days} d`;
}

export default function Dashboard() {
  const { user } = useAdminAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentContacts, setRecentContacts] = useState<ContactRequest[]>([]);
  const [activity, setActivity] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        proyectosRes,
        serviciosRes,
        redesRes,
        imagenesRes,
        contenidoRes,
        mensajesRes,
        contactosRes,
        actividadRes,
      ] = await Promise.all([
        supabase.from("projects").select("id, estado", { count: "exact" }),
        supabase.from("services").select("id", { count: "exact", head: true }).eq("activo", true),
        supabase.from("social_links").select("id", { count: "exact", head: true }).eq("activo", true),
        supabase.from("media").select("id", { count: "exact", head: true }),
        supabase.from("content_sections").select("id", { count: "exact", head: true }),
        supabase.from("contact_requests").select("id", { count: "exact", head: true }),
        supabase.from("contact_requests").select("*").order("created_at", { ascending: false }).limit(5),
        supabase.from("activity_logs").select("*").order("created_at", { ascending: false }).limit(6),
      ]);

      const proyectos = proyectosRes.data ?? [];
      const publicados = proyectos.filter((p) => p.estado === "publicado").length;

      setStats({
        proyectos: proyectos.length,
        proyectosPublicados: publicados,
        servicios: serviciosRes.count ?? 0,
        redes: redesRes.count ?? 0,
        imagenes: imagenesRes.count ?? 0,
        contenido: contenidoRes.count ?? 0,
        mensajes: mensajesRes.count ?? 0,
      });
      setRecentContacts((contactosRes.data ?? []) as ContactRequest[]);
      setActivity((actividadRes.data ?? []) as ActivityLog[]);
    } catch {
      setError("No se pudieron cargar los datos del panel.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <div className="w-10 h-10 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin" />
        <p className="text-sm text-foreground-600">Cargando panel…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
        <div className="w-14 h-14 rounded-full bg-red-50 text-red-500 flex items-center justify-center">
          <i className="ri-error-warning-line text-2xl" />
        </div>
        <p className="text-sm text-foreground-700">{error}</p>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium whitespace-nowrap"
        >
          <i className="ri-refresh-line" />
          Reintentar
        </button>
      </div>
    );
  }

  const statCards = [
    { label: "Proyectos publicados", value: stats?.proyectosPublicados ?? 0, icon: "ri-rocket-2-line", accent: "text-primary-600 bg-primary-50" },
    { label: "Servicios", value: stats?.servicios ?? 0, icon: "ri-stack-line", accent: "text-accent-600 bg-accent-50" },
    { label: "Redes sociales", value: stats?.redes ?? 0, icon: "ri-share-line", accent: "text-secondary-700 bg-secondary-100" },
    { label: "Imágenes", value: stats?.imagenes ?? 0, icon: "ri-image-line", accent: "text-primary-600 bg-primary-50" },
    { label: "Contenido del sitio", value: stats?.contenido ?? 0, icon: "ri-edit-line", accent: "text-accent-600 bg-accent-50" },
    { label: "Mensajes / contactos", value: stats?.mensajes ?? 0, icon: "ri-mail-line", accent: "text-secondary-700 bg-secondary-100" },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-foreground-950">MARK DIRECT COMPANY</h1>
        <p className="text-sm text-foreground-500 mt-1">
          Panel de administración — Hola, {user?.user_metadata?.name || "Administrador"} 👋
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((card) => (
          <div key={card.label} className="bg-white border border-background-200 rounded-lg p-5 flex items-center gap-4">
            <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${card.accent}`}>
              <i className={`${card.icon} text-xl`} />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold text-foreground-950">{card.value}</p>
              <p className="text-sm text-foreground-500">{card.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div>
        <h2 className="font-heading text-base font-semibold text-foreground-900 mb-3">Accesos rápidos</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {quickAccess.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              className="bg-white border border-background-200 rounded-lg p-4 text-center hover:border-primary-300 transition-colors"
            >
              <div className="w-9 h-9 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center mx-auto mb-2">
                <i className={`${item.icon} text-lg`} />
              </div>
              <p className="text-xs font-medium text-foreground-700 leading-tight">{item.label}</p>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-background-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-base font-semibold text-foreground-900">Actividad reciente</h2>
            <Link to="/admin/historial" className="text-xs font-medium text-primary-600 hover:text-primary-700">
              Ver todo
            </Link>
          </div>
          {activity.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-sm text-foreground-400">Aún no hay actividad registrada.</p>
              <p className="text-xs text-foreground-400 mt-1">Tus cambios aparecerán aquí.</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {activity.map((log) => (
                <li key={log.id} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary-500 mt-1.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-foreground-800 leading-snug">
                      <span className="font-medium">{log.usuario || "Admin"}</span> {log.accion}{" "}
                      {log.elemento && <span className="text-foreground-600">{log.elemento}</span>}
                    </p>
                    <p className="text-xs text-foreground-400 mt-0.5">{timeAgo(log.created_at)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-background-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-base font-semibold text-foreground-900">Solicitudes recientes</h2>
            <Link to="/admin/solicitudes" className="text-xs font-medium text-primary-600 hover:text-primary-700">
              Ver todas
            </Link>
          </div>
          {recentContacts.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-sm text-foreground-400">Aún no hay solicitudes de contacto.</p>
              <p className="text-xs text-foreground-400 mt-1">Cuando alguien complete el formulario aparecerá aquí.</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {recentContacts.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground-900 truncate">{c.nombre}</p>
                    <p className="text-xs text-foreground-500 truncate">
                      {c.tipo_proyecto || "Sin tipo"} · {c.email || "Sin email"}
                    </p>
                  </div>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">{timeAgo(c.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}